import * as THREE from 'three';
import { MindARThree } from '../../vendor/mindar-image-three.prod.js';
import { IAREngine, IARTracker, IARAnchor, IARCamera } from '../ar-engine.interface';
import { AREngineConfig, TrackerConfig, PermissionResult, LoadingProgressCallback, TrackerState, VisibilityCallback, AREngineCapabilities } from '../ar-types';

/**
 * MindAR Camera Adapter
 */
class MindARCameraAdapter implements IARCamera {
  constructor(
    private mindARCamera: THREE.Camera,
    private mindARInstance: any
  ) {}

  getThreeCamera(): THREE.Camera {
    return this.mindARCamera;
  }

  async start(): Promise<void> {
    // MindAR starts when the main instance starts
  }

  stop(): void {
    // MindAR stops when the main instance stops
  }

  updateFrame(renderer: THREE.WebGLRenderer): void {
    // MindAR handles frame updates internally
  }

  getBackgroundTexture(): THREE.Texture | null {
    // MindAR handles background internally
    return null;
  }

  async switchCamera(): Promise<void> {
    if (this.mindARInstance.switchCamera) {
      await this.mindARInstance.switchCamera();
    }
  }
}

/**
 * MindAR Anchor Adapter
 */
class MindARAnchorAdapter implements IARAnchor {
  constructor(
    private anchorGroup: THREE.Group,
    private trackerId: string
  ) {}

  getGroup(): THREE.Group {
    return this.anchorGroup;
  }

  getTrackerId(): string {
    return this.trackerId;
  }

  dispose(): void {
    if (this.anchorGroup.parent) {
      this.anchorGroup.parent.remove(this.anchorGroup);
    }
  }
}

/**
 * MindAR Tracker Adapter
 */
class MindARTrackerAdapter implements IARTracker {
  private anchor: MindARAnchorAdapter | null = null;
  private currentState: TrackerState = TrackerState.NOT_VISIBLE;
  private visibilityCallbacks: Set<VisibilityCallback> = new Set();

  constructor(
    private mindARAnchor: any,
    private config: TrackerConfig
  ) {
    console.log(`   🔗 Setting up tracker adapter for: ${config.name}`);
    console.log('      Anchor exists:', !!mindARAnchor);
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    console.log(`   🎣 Setting up event handlers for: ${this.config.name}`);

    // MindAR uses onTargetFound and onTargetLost callbacks directly on the anchor
    if (this.mindARAnchor) {
      console.log('      Anchor is valid, attaching callbacks...');

      this.mindARAnchor.onTargetFound = () => {
        console.log(`🎯 ========== TARGET FOUND: ${this.config.name} ==========`);
        this.currentState = TrackerState.VISIBLE;
        this.notifyVisibilityCallbacks();
      };

      this.mindARAnchor.onTargetLost = () => {
        console.log(`🔍 ========== TARGET LOST: ${this.config.name} ==========`);
        this.currentState = TrackerState.NOT_VISIBLE;
        this.notifyVisibilityCallbacks();
      };

      console.log('      ✅ Callbacks attached successfully');
    } else {
      console.error('      ❌ Anchor is null/undefined, cannot attach callbacks!');
    }
  }

  private notifyVisibilityCallbacks(): void {
    this.visibilityCallbacks.forEach(callback => {
      callback(this.config.id, this.currentState);
    });
  }

  getId(): string {
    return this.config.id;
  }

  getConfig(): TrackerConfig {
    return this.config;
  }

  getState(): TrackerState {
    return this.currentState;
  }

  createAnchor(): IARAnchor {
    if (this.anchor) {
      return this.anchor;
    }

    this.anchor = new MindARAnchorAdapter(this.mindARAnchor.group, this.config.id);
    return this.anchor;
  }

  onVisibilityChange(callback: VisibilityCallback): void {
    this.visibilityCallbacks.add(callback);
  }

  offVisibilityChange(callback: VisibilityCallback): void {
    this.visibilityCallbacks.delete(callback);
  }

  dispose(): void {
    if (this.anchor) {
      this.anchor.dispose();
      this.anchor = null;
    }
    this.visibilityCallbacks.clear();
  }
}

/**
 * MindAR AR Engine Implementation
 */
export class MindAREngine implements IAREngine {
  private config: AREngineConfig | null = null;
  private camera: MindARCameraAdapter | null = null;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private mindARInstance: any = null;
  private trackers: Map<string, MindARTrackerAdapter> = new Map();
  private targetFiles: Map<number, string> = new Map(); // Map target index to tracker ID
  private isStarted: boolean = false;

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene;
    this.renderer = renderer;
  }

  async init(config: AREngineConfig): Promise<void> {
    this.config = config;

    console.log('✅ MindAR engine initialized (instance will be created when first tracker is added)');
  }

  private async ensureMindARInstance(targetFile: string, onProgress?: LoadingProgressCallback): Promise<void> {
    if (this.mindARInstance) {
      console.log('🔄 MindAR instance already exists');
      return;
    }

    console.log('🚀 Creating MindAR instance...');
    console.log('   Target file:', targetFile);
    onProgress?.('Creating AR instance', 0.4);

    const container = this.config?.container || document.body;

    // MindAR configuration
    const mindARConfig: any = {
      container: container,
      imageTargetSrc: targetFile
    };

    // Add optional MindAR-specific settings
    if (this.config?.engineOptions?.mindar) {
      const mindAROptions = this.config.engineOptions.mindar;
      if (mindAROptions.filterMinCF !== undefined) {
        mindARConfig.filterMinCF = mindAROptions.filterMinCF;
      }
      if (mindAROptions.filterBeta !== undefined) {
        mindARConfig.filterBeta = mindAROptions.filterBeta;
      }
      if (mindAROptions.missTolerance !== undefined) {
        mindARConfig.missTolerance = mindAROptions.missTolerance;
      }
      if (mindAROptions.warmupTolerance !== undefined) {
        mindARConfig.warmupTolerance = mindAROptions.warmupTolerance;
      }
      if (mindAROptions.maxTrack !== undefined) {
        mindARConfig.maxTrack = mindAROptions.maxTrack;
      }
      if (mindAROptions.uiLoading !== undefined) {
        mindARConfig.uiLoading = mindAROptions.uiLoading;
      }
      if (mindAROptions.uiScanning !== undefined) {
        mindARConfig.uiScanning = mindAROptions.uiScanning;
      }
    }

    console.log('🧪 Full MindAR config:', JSON.stringify(mindARConfig, null, 2));
    console.log('🎨 UI Overlay Settings:');
    console.log('   uiLoading:', mindARConfig.uiLoading);
    console.log('   uiScanning:', mindARConfig.uiScanning);

    // Create MindAR instance
    console.log('   Creating new MindARThree instance...');
    onProgress?.('Initializing tracking system', 0.5);
    this.mindARInstance = new MindARThree(mindARConfig);
    console.log('   MindARThree instance created:', !!this.mindARInstance);

    // Report progress: Setting up camera and scene
    onProgress?.('Setting up camera and scene', 0.8);

    // Check if overlay elements were created
    setTimeout(() => {
      const scanningOverlay = document.querySelector('.mindar-ui-scanning');
      const loadingOverlay = document.querySelector('.mindar-ui-loading');
      console.log('🔍 Checking for MindAR overlay elements in DOM:');
      console.log('   Scanning overlay element:', !!scanningOverlay);
      console.log('   Loading overlay element:', !!loadingOverlay);
      if (scanningOverlay) {
        const styles = window.getComputedStyle(scanningOverlay);
        console.log('   Scanning overlay display:', styles.display);
        console.log('   Scanning overlay visibility:', styles.visibility);
        console.log('   Scanning overlay opacity:', styles.opacity);
      }
    }, 500);

    // Get the camera, scene, and renderer from MindAR
    const { renderer: mindRenderer, scene: mindScene, camera: mindCamera } = this.mindARInstance;
    console.log('   Got renderer:', !!mindRenderer);
    console.log('   Got scene:', !!mindScene);
    console.log('   Got camera:', !!mindCamera);

    // MindAR creates its own renderer, scene, and camera
    // We need to use those instead of the ones passed to constructor
    this.renderer = mindRenderer;
    this.scene = mindScene;
    this.camera = new MindARCameraAdapter(mindCamera, this.mindARInstance);

    console.log('✅ MindAR instance fully initialized');
  }

  getCapabilities(): AREngineCapabilities {
    return {
      multipleTrackers: true, // MindAR supports multiple targets in one .mind file
      cameraSwitch: true,
      targetFormat: '.mind',
      maxTrackers: 5 // MindAR recommends not too many simultaneous trackers
    };
  }

  isCompatible(): boolean {
    // Check for WebGL and getUserMedia support
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      const hasWebGL = !!gl;
      const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      return hasWebGL && hasGetUserMedia;
    } catch (e) {
      return false;
    }
  }

  showIncompatibilityUI(): void {
    const message = 'Your browser does not support WebAR. Please use a modern browser with WebGL and camera support.';
    alert(message);
    console.error(message);
  }

  async requestPermissions(): Promise<PermissionResult> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      // Stop the stream immediately, we just wanted to check permissions
      stream.getTracks().forEach(track => track.stop());
      return { granted: true };
    } catch (error) {
      return {
        granted: false,
        error: error instanceof Error ? error.message : 'Camera permission denied'
      };
    }
  }

  showPermissionDeniedUI(): void {
    const message = 'Camera permission denied. Please allow camera access to use AR features.';
    alert(message);
    console.error(message);
  }

  getCamera(): IARCamera {
    if (!this.camera) {
      // MindAR requires a tracker to be created first before we have a camera
      // Return a stub that will throw an error if used before proper initialization
      throw new Error('MindAR camera not available yet. Create a tracker first using createTracker().');
    }
    return this.camera;
  }

  getScene(): THREE.Scene {
    return this.scene;
  }

  getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  async createTracker(config: TrackerConfig): Promise<IARTracker> {
    console.log(`📍 Creating tracker: ${config.name} (${config.id})`);

    if (this.trackers.has(config.id)) {
      throw new Error(`Tracker with id '${config.id}' already exists`);
    }

    // Report progress: Starting tracker creation
    config.onProgress?.('Creating tracker', 0.1);

    // Ensure MindAR instance is created
    console.log('   Ensuring MindAR instance...');
    config.onProgress?.('Initializing AR engine', 0.3);
    await this.ensureMindARInstance(config.targetFile, config.onProgress);

    // Get target index (default to 0)
    const targetIndex = config.targetIndex ?? 0;
    console.log(`   Target index: ${targetIndex}`);

    // Report progress: Adding anchor
    config.onProgress?.('Adding anchor', 0.7);
    console.log('   Adding anchor to MindAR...');
    const mindARAnchor = this.mindARInstance.addAnchor(targetIndex);
    console.log('   Anchor created:', !!mindARAnchor);
    console.log('   Anchor group:', !!mindARAnchor?.group);

    // Report progress: Final setup
    config.onProgress?.('Finalizing tracker setup', 0.9);

    // Create adapter
    const trackerAdapter = new MindARTrackerAdapter(mindARAnchor, config);

    this.trackers.set(config.id, trackerAdapter);
    this.targetFiles.set(targetIndex, config.id);

    console.log(`✅ Tracker created: ${config.name}`);
    config.onProgress?.('Tracker ready', 1.0);

    return trackerAdapter;
  }

  getTracker(id: string): IARTracker | undefined {
    return this.trackers.get(id);
  }

  getAllTrackers(): IARTracker[] {
    return Array.from(this.trackers.values());
  }

  removeTracker(id: string): void {
    const tracker = this.trackers.get(id);
    if (tracker) {
      tracker.dispose();
      this.trackers.delete(id);
      console.log(`🗑️ MindAR tracker removed: ${id}`);
    }
  }

  async start(): Promise<void> {
    console.log('🎬 Starting MindAR...');

    if (!this.mindARInstance) {
      throw new Error('No trackers created. Create at least one tracker before starting.');
    }

    if (this.isStarted) {
      console.warn('⚠️ MindAR already started');
      return;
    }

    console.log('   Calling mindARInstance.start()...');
    await this.mindARInstance.start();
    this.isStarted = true;

    console.log('✅ MindAR started successfully');
    console.log('   Active trackers:', this.trackers.size);

    // Check overlay visibility after start
    setTimeout(() => {
      const scanningOverlay = document.querySelector('.mindar-ui-scanning');
      console.log('🔍 Overlay check after start():');
      console.log('   Scanning overlay exists:', !!scanningOverlay);
      if (scanningOverlay) {
        const styles = window.getComputedStyle(scanningOverlay);
        console.log('   Display:', styles.display);
        console.log('   Visibility:', styles.visibility);
        console.log('   Opacity:', styles.opacity);
        console.log('   Z-index:', styles.zIndex);
      }
      // List all elements with 'mindar' in className
      const allMindARElements = document.querySelectorAll('[class*="mindar"]');
      console.log('   All MindAR elements in DOM:', allMindARElements.length);
      allMindARElements.forEach((el, i) => {
        console.log(`     [${i}] ${el.className}`);
      });
    }, 500);
  }

  stop(): void {
    if (this.mindARInstance && this.isStarted) {
      this.mindARInstance.stop();
      this.isStarted = false;
      console.log('⏸️ MindAR stopped');
    }
  }

  update(renderer: THREE.WebGLRenderer): void {
    // MindAR handles updates internally in its animation loop
    // No need for manual updates

    // Debug: Log update calls every 60 frames (~1 second at 60fps)
    if (!this._updateCount) this._updateCount = 0;
    this._updateCount++;
    if (this._updateCount % 600 === 0) {
      console.log(`🔄 MindAR update loop running (frame ${this._updateCount})`);
      console.log('   Started:', this.isStarted);
      console.log('   Trackers:', this.trackers.size);
    }
  }

  private _updateCount: number = 0;

  dispose(): void {
    // Dispose all trackers
    this.trackers.forEach(tracker => tracker.dispose());
    this.trackers.clear();
    this.targetFiles.clear();

    // Stop MindAR
    this.stop();

    this.camera = null;
    this.mindARInstance = null;
    this.config = null;

    console.log('🗑️ MindAR engine disposed');
  }
}
